import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Headset } from "@phosphor-icons/react";

const STATUSES = ["NEW", "ASSIGNED", "IN_PROGRESS", "WAITING", "RESOLVED", "CLOSED"];
const PRIOS = ["LOW", "MEDIUM", "HIGH", "URGENT"];
const PCOLOR = { URGENT: "bg-red-100 text-red-700", HIGH: "bg-orange-100 text-orange-700", MEDIUM: "bg-blue-100 text-blue-700", LOW: "bg-stone-100 text-stone-600" };

export default function AdminSupport() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [note, setNote] = useState({});
  const load = () => api.get("/admin/support", { params: filter ? { status: filter } : {} }).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load tickets"));
  useEffect(() => { load(); }, [filter]);

  const patch = (t, body) => api.patch(`/admin/support/${t.id}`, body).then(() => { toast.success("Updated"); setNote({}); load(); }).catch(() => toast.error("Failed"));

  if (!data) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;
  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center gap-2"><Headset size={26} className="text-primary" /><h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Support Tickets</h1></div>
      <div className="mt-4 flex flex-wrap gap-2">
        {["", ...STATUSES].map((s) => <button key={s} data-testid={`support-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1.5 text-xs font-medium ${filter === s ? "bg-primary text-white" : "border border-stone-200 text-stone-600 hover:bg-accent"}`}>{s || "All"}</button>)}
      </div>
      <div className="mt-4 space-y-3" data-testid="support-list">
        {data.tickets.length === 0 ? <p className="rounded-xl border border-dashed border-stone-200 p-6 text-center text-sm text-stone-400">No tickets.</p> :
          data.tickets.map((t) => (
            <div key={t.id} className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={`ticket-${t.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PCOLOR[t.priority]}`}>{t.priority}</span>
                <span className="font-medium text-stone-800">{t.ticket_ref} · {t.subject}</span>
                <span className="text-xs text-stone-400">{t.user_role} {t.user_name} · {t.category}</span>
                <span className="ml-auto rounded-full bg-stone-100 px-2 py-0.5 text-xs">{t.status}</span>
              </div>
              <p className="mt-2 text-sm text-stone-600">{t.description}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select data-testid={`ticket-${t.id}-status`} value={t.status} onChange={(e) => patch(t, { status: e.target.value })} className="rounded-lg border border-stone-200 px-2 py-1 text-xs">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                <select data-testid={`ticket-${t.id}-priority`} value={t.priority} onChange={(e) => patch(t, { priority: e.target.value })} className="rounded-lg border border-stone-200 px-2 py-1 text-xs">{PRIOS.map((s) => <option key={s}>{s}</option>)}</select>
                <Input data-testid={`ticket-${t.id}-note`} placeholder="Add note" value={note[t.id] || ""} onChange={(e) => setNote({ ...note, [t.id]: e.target.value })} className="h-8 w-48" />
                <button data-testid={`ticket-${t.id}-note-save`} onClick={() => patch(t, { note: note[t.id] })} className="rounded-md bg-primary px-2 py-1 text-xs text-white">Add</button>
              </div>
              {(t.notes || []).length > 0 && <div className="mt-2 space-y-1 text-xs text-stone-500">{t.notes.map((n, i) => <p key={i}>• {n.text}</p>)}</div>}
            </div>
          ))}
      </div>
    </DashboardLayout>
  );
}
